import { grantsFreeMagnets, grantsFreeItem, freeAddonsOf } from '../../../../utils/offerPricing';
import { routes } from '../../../../routes';

// Decide whether to show the "Your 2 FREE photo magnets" page after a package is
// built and before Announcements & Gifts (issue #5218 item 8). A pure function so
// the gate is unit-testable, and asked at EVERY entry point: elementary's built
// package, its All Digital fast path, its gallery Download-all card, and the
// senior builder's post-build funnel.
//
// AUDIENCE-AGNOSTIC despite living under _ElementaryMultiPose (where it was
// first written -- the files stayed put so the move didn't bury the wiring
// change in renames). Nothing in here reads the audience; the offer decides,
// and a senior offer that grants magnets is answered exactly like an
// elementary one.
//
// `qualifies` / `hasFreeMagnets` are EXPLICIT, FRESH booleans (from
// freeMagnetsStatus -- the build/buy-all response or a re-read of the cart
// after the package exists). Never read off a cached cart object: the React
// Query cache and CustomerContext predate the package that was just built,
// and their `qualifiesForFreeMagnets` was a stale false that hid the page
// for a family's first package.
//
// Shown only when ALL of:
//   - the live offer grants free magnets (grantsFreeMagnets -- the ONE
//     interpreter; nothing else decides), AND
//   - the cart qualifies (explicitly true), AND
//   - the cart does not already carry the offer's free sheet, AND
//   - the session is not all-JPG (those poses have their background baked in
//     and are not sold on magnets, exactly as the Buy All card is hidden).
//
// The server re-checks on accept (Cart#sync_free_magnets!), so a wrong answer
// here can only show the page, never attach a sheet the offer does not back.
export default function shouldShowFreeMagnets({
  offer, qualifies, hasFreeMagnets, allJpg,
}) {
  if (!grantsFreeMagnets(offer)) return false;
  if (qualifies !== true) return false;
  if (hasFreeMagnets) return false;
  if (allJpg) return false;
  return true;
}

// ---- Free item of your choice (#5747) ---------------------------------------
//
// Should THIS order package stop at the free-item picker? Per PACKAGE, unlike
// the magnet tier's per-cart gate above: two children on one order each get
// one, so the question is asked after every build with that build's id.
//
// `freeItemPackages` is the FRESH per-package list (freeMagnetsStatus -- the
// build/buy-all response or a re-read of the cart after the package exists),
// never the cached cart, for the same reason as the magnet flags.
//
// Shown only when ALL of:
//   - the live offer grants a free item (grantsFreeItem -- the ONE interpreter), AND
//   - this package is in the offer's list (it qualifies), AND
//   - it has no pick yet (addon_id null), AND
//   - the session is not all-JPG while EVERY design in the set refuses JPGs
//     (nothing could be put on any of them; a set with one framed design
//     still shows, and the page itself filters per design).
//
// The server re-checks on accept (Cart#grant_free_item!), so a wrong answer
// here can only show the page, never attach a sheet the offer does not back.
export function shouldShowFreeItem({
  offer, orderPackageId, freeItemPackages, allJpg,
}) {
  if (!grantsFreeItem(offer)) return false;
  const entry = (freeItemPackages || []).find((p) => String(p?.order_package_id) === String(orderPackageId));
  if (!entry) return false;
  if (entry.addon_id != null) return false;
  if (allJpg && freeAddonsOf(offer).every((a) => a?.allowsJpgPoses === false)) return false;
  return true;
}

// May the page take its show/skip decision yet? The skip effect must never run
// on placeholder data: usePoses serves placeholderData [] while the real fetch
// is in flight, and deciding on it skipped the page for every customer (the
// Timber Point canary, 2026-09-01). Pure so the readiness contract is testable.
export function readyToDecide({
  loadingPackage, packageId, loadingOffer, loadingCart, poses, posesPlaceholder,
  addonsPlaceholder = false, backgroundsPlaceholder = false,
}) {
  if (loadingPackage || loadingOffer || loadingCart) return false;
  if (!packageId) return false;
  if (!poses || posesPlaceholder) return false;
  // useGiftsPageAddons / useBackgrounds also serve placeholderData [] while
  // fetching; rendering on them flashes fallback art/value and an absent
  // background strip (review on #5264). Same rule as poses: real data only.
  if (addonsPlaceholder || backgroundsPlaceholder) return false;
  return true;
}

// Where to go after a package lands in the cart: the magnet page (carrying
// `next`, where it continues to) when the gate says so, else `next` itself.
// `status` is freeMagnetsStatus()'s { qualifies, hasFreeMagnets }. ONE
// implementation for every entry point in both flows -- the senior builder
// funnels its three post-build paths through this too, so the magnet page can
// never appear in one flow and be forgotten in the other.
//
// The senior flow enters here AFTER the $99 Buy All upsell rather than before
// it: goAfterBuild is the single funnel every senior path already converges on
// (straight-to-add-ons, Buy All's continue, and Buy All's just-added), so
// hooking it there covers all three with one call and leaves the paid upsell
// exactly where it was. A free gift also reads better after the paid ask than
// interrupting it.

export function afterBuildFreeMagnetsRoute({
  next, orderPackageId, offer, status, allJpg,
}) {
  if (!orderPackageId) return next;
  // The free-item offer (#5747) takes the same stop, per package. ONE gate
  // for both offers, so neither can be remembered in one flow and forgotten
  // in the other.
  if (shouldShowFreeItem({
    offer, orderPackageId, freeItemPackages: status?.freeItemPackages, allJpg,
  })) {
    return routes.freeItem(orderPackageId, { next });
  }
  const show = shouldShowFreeMagnets({
    offer, qualifies: status?.qualifies, hasFreeMagnets: status?.hasFreeMagnets, allJpg,
  });
  if (!show) return next;
  return routes.freeMagnets(orderPackageId, { next });
}

// The whole post-build decision, INCLUDING whether the cart even needs reading.
// Extracted from the senior builder so the branching is testable: a component
// with a dozen hooks is not, and this is the only genuinely new behaviour in the
// senior wiring.
//
// Three rules, in order:
//   1. No magnet-granting offer -> `next`, WITHOUT touching the network. The
//      common case (no offer, or a percentage tier), and the status read would
//      only be discarded by the gate.
//   2. The status read failed -> `next`. The package is already in the cart by
//      this point, so this navigation has to happen regardless; a stale cart id
//      404s (the case cartSelfHeal exists for) and an unhandled rejection would
//      strand the customer on a spinner with their package created.
//   3. Otherwise the gate decides, from the FRESH flags.
//
// `readStatus` is injected so a test can drive all three without a fake cart.
export async function magnetStopDestination({
  next, orderPackageId, offer, allJpg, cartId, readStatus, onStatusError,
}) {
  if (!grantsFreeMagnets(offer) && !grantsFreeItem(offer)) return next;

  let status;
  try {
    status = await readStatus({ cartId });
  } catch (err) {
    if (onStatusError) onStatusError(err);
    return next;
  }

  return afterBuildFreeMagnetsRoute({
    next, orderPackageId, offer, status, allJpg,
  });
}
